/**
 * Modificadores: la semántica de "modificador global" en UN solo lugar.
 *
 * EL MODELO (raro, pero es el que hay): `product_modifiers` NO tiene tenant_id y una fila es
 * (product_id, slug). Un modificador "global" del tenant es en realidad N filas, una por
 * producto, unidas por el mismo `slug`. El scoping multi-tenant, entonces, solo existe a
 * través de `products.tenant_id`.
 *
 * POR QUÉ EXISTE ESTE MÓDULO: esa semántica estaba copiada a mano en ModifiersPage (borrar) y
 * ModifierEditPage (editar), y se desincronizaron. En #246 hice el borrado coherente con la
 * edición... tomando como referencia una línea que estaba rota. Los bugs que cierra:
 *
 *   1. TRUNCACIÓN: ambas páginas resolvían el scope con `select('id').eq('tenant_id')` SIN
 *      paginar, y PostgREST corta en 1000 (max-rows). Con catálogo grande, los modificadores
 *      de los productos posteriores al #1000 "no existían": la edición no los encontraba y la
 *      página te expulsaba con "Modificador no encontrado".
 *   2. GUARDADO FANTASMA: el update filtraba por esa lista truncada, no afectaba NINGUNA fila
 *      y aun así se cantaba "Modificador actualizado". El usuario se iba creyendo que guardó.
 *   3. URI TOO LONG: meter miles de uuids en un solo `.in()` revienta la URL (414).
 *
 * EL ENFOQUE: no se trae el catálogo entero para filtrar. Se piden las filas DEL MODIFICADOR
 * scopeadas por el join (`products!inner` + `products.tenant_id`, patrón que el repo ya usa en
 * useWaDeepSearch y crm/sequences) y se muta por `id`. Se pagina igual, porque un modificador
 * presente en >1000 productos son >1000 filas.
 */
import { supabase } from '@/lib/supabase';

/** Filas por request: un `.in()` con miles de uuids revienta la URL (414). */
const ID_CHUNK = 300;
/** Tope duro de PostgREST por request. */
const PAGE = 1000;

export interface ModifierRow {
  id: string;
  name: string;
  slug: string | null;
  price_adjustment: number;
}

/** Identidad de un modificador global. `slug` manda; `name` es el fallback de filas legacy. */
export interface ModifierIdentity {
  slug: string;
  name: string;
}

/**
 * Separa identidades con slug de las legacy (sin slug, solo name). PURA.
 * Las filas viejas no tienen slug: sin este fallback no se las encuentra y quedan huérfanas.
 */
export function splitModifierIdentities(items: ModifierIdentity[]): { slugs: string[]; names: string[] } {
  return {
    slugs: [...new Set(items.map((i) => i.slug).filter(Boolean))],
    names: [...new Set(items.filter((i) => !i.slug).map((i) => i.name).filter(Boolean))],
  };
}

/** Parte una lista en bloques de `size`. PURA. */
export function chunk<T>(arr: T[], size = ID_CHUNK): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** Query base scopeada al tenant por el join. Única fuente del scoping multi-tenant. */
const scoped = (columns: string, tenantId: string) =>
  supabase
    .from('product_modifiers')
    .select(`${columns}, products!inner(tenant_id)`)
    .eq('products.tenant_id', tenantId);

/**
 * Un modificador del tenant por slug (cualquiera de sus N filas: todas comparten los datos).
 * `null` si no existe. Sin truncación: no depende de listar el catálogo.
 */
export async function fetchModifierBySlug(tenantId: string, slug: string): Promise<ModifierRow | null> {
  const { data, error } = await scoped('id, name, slug, price_adjustment', tenantId)
    .eq('slug', slug)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as ModifierRow) ?? null;
}

/** Un modificador del tenant por id de fila (ruta legacy `/modifiers/<uuid>/edit`). */
export async function fetchModifierById(tenantId: string, id: string): Promise<ModifierRow | null> {
  const { data, error } = await scoped('id, name, slug, price_adjustment', tenantId)
    .eq('id', id)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as ModifierRow) ?? null;
}

/** Ids de TODAS las filas de esas identidades, paginado (un modificador = N filas). */
async function fetchRowIdsByIdentity(tenantId: string, items: ModifierIdentity[]): Promise<string[]> {
  const { slugs, names } = splitModifierIdentities(items);
  const ids: string[] = [];

  const collect = async (column: 'slug' | 'name', values: string[]) => {
    if (values.length === 0) return;
    for (let offset = 0; ; offset += PAGE) {
      const { data, error } = await scoped('id', tenantId)
        .in(column, values)
        .order('id', { ascending: true })
        .range(offset, offset + PAGE - 1);
      if (error) throw error;
      const rows = (data ?? []) as unknown as { id: string }[];
      ids.push(...rows.map((r) => r.id));
      if (rows.length < PAGE) break;
    }
  };

  await collect('slug', slugs);
  await collect('name', names);
  return [...new Set(ids)];
}

/** Borra un modificador global en TODAS sus filas (una por producto). */
export async function deleteModifiersByIdentity(tenantId: string, items: ModifierIdentity[]): Promise<void> {
  if (!tenantId || items.length === 0) return;
  const ids = await fetchRowIdsByIdentity(tenantId, items);
  for (const c of chunk(ids)) {
    const { error } = await supabase.from('product_modifiers').delete().in('id', c);
    if (error) throw error;
  }
}

export interface ModifierUpdate {
  /** Slug con el que se cargó: es su identidad ACTUAL en la BD. */
  originalSlug: string;
  name: string;
  slug: string;
  priceAdjustment: number;
}

/**
 * Actualiza un modificador global en TODAS sus filas. Devuelve cuántas filas cambió.
 *
 * El contador NO es cosmético: antes el update filtraba por una lista truncada, no tocaba
 * ninguna fila, y la página igual cantaba "Modificador actualizado". Con el conteo, quien
 * llama distingue "guardado" de "no se guardó nada" y deja de mentir.
 */
export async function updateModifierByIdentity(tenantId: string, u: ModifierUpdate): Promise<number> {
  if (!tenantId || !u.originalSlug) return 0;
  const ids = await fetchRowIdsByIdentity(tenantId, [{ slug: u.originalSlug, name: u.name }]);
  let affected = 0;
  for (const c of chunk(ids)) {
    const { data, error } = await supabase
      .from('product_modifiers')
      .update({ name: u.name, slug: u.slug, price_adjustment: u.priceAdjustment })
      .in('id', c)
      .select('id');
    if (error) throw error;
    affected += (data ?? []).length;
  }
  return affected;
}
